import { createUID } from '@pega/cosmos-react-core';
import type {
  AutoCompleteTriggerChar,
  CursorPosition,
  ExpressionBuilderProps
} from '@pega/cosmos-react-build';

export const getFilterRegex = (inputValue: string) => {
  return new RegExp(`${inputValue.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&')}`, 'i');
};

export const errors =
  'Error message 1. Some unrealistic error message. Server unreachable. Check your network connection and try again';

export const items = [
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Validate loan value',
    type: 'Decision',
    expression: '@validateLoanValue()',
    details: {
      primary: [
        {
          field: 'Description',
          value:
            'Function to validate loan value through simple interest or compound interest based on user choice'
        },
        {
          field: 'Scope',
          value: 'CCDBankLoan'
        },
        {
          field: 'Namespace',
          value: 'px'
        }
      ],
      output: [
        {
          field: 'isValid',
          value: 'Boolean'
        }
      ],
      input: [
        {
          id: 'accountNumber',
          field: 'Account number',
          type: 'text'
        },
        {
          id: 'term',
          field: 'Term in years',
          type: 'number'
        },
        {
          id: 'accountType',
          field: 'Account type',
          type: 'text'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Loan amount under 100k',
    type: 'When',
    expression: '@isLoanAmountUnder100k()',
    details: {
      primary: [
        {
          field: 'Scope',
          value: 'CCDBankLoan'
        },
        {
          field: 'Namespace',
          value: 'px'
        }
      ],
      output: [
        {
          field: 'isUnder100k',
          value: 'Boolean'
        }
      ],
      input: [
        {
          id: 'accountNumber',
          field: 'Account number',
          type: 'text'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Loan number',
    type: 'Field',
    expression: '.loanNumber',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Loan account number of current customer'
        },
        {
          field: 'Scope',
          value: 'CCDBankLoan'
        },
        {
          field: 'Namespace',
          value: 'px'
        }
      ],
      output: []
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Customer name',
    type: 'Field',
    expression: '.customerName',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Fullname of current customer'
        },
        {
          field: 'Scope',
          value: 'CCDBankLoan'
        },
        {
          field: 'Namespace',
          value: 'px'
        }
      ],
      output: []
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Max',
    type: 'Function',
    expression: 'Math@Function:max()',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Returns max of two numbers'
        },
        {
          field: 'Scope',
          value: 'Math'
        },
        {
          field: 'Namespace',
          value: 'py'
        }
      ],
      output: [
        {
          field: 'max',
          value: 'Number'
        }
      ],
      input: [
        {
          id: 'param1',
          field: 'Param 1',
          type: 'number'
        },
        {
          id: 'param2',
          field: 'Param 2',
          type: 'number'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Min',
    type: 'Function',
    expression: 'Math@Function:min()',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Return min of two numbers'
        },
        {
          field: 'Scope',
          value: 'Math'
        },
        {
          field: 'Namespace',
          value: 'py'
        }
      ],
      output: [
        {
          field: 'min',
          value: 'Number'
        }
      ],
      input: [
        {
          id: 'param1',
          field: 'Param 1',
          type: 'number'
        },
        {
          id: 'param2',
          field: 'Param 2',
          type: 'number'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Sum',
    type: 'Function',
    expression: 'Math@Function:sum()',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Return sum of two numbers'
        },
        {
          field: 'Scope',
          value: 'Math'
        },
        {
          field: 'Namespace',
          value: 'py'
        }
      ],
      output: [
        {
          field: 'sum',
          value: 'Number'
        }
      ],
      input: [
        {
          id: 'param1',
          field: 'Param 1',
          type: 'number'
        },
        {
          id: 'param2',
          field: 'Param 2',
          type: 'number'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Absolute',
    type: 'Function',
    expression: 'Math@Function:abs()',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Return absolute value of a number'
        },
        {
          field: 'Scope',
          value: 'Math'
        },
        {
          field: 'Namespace',
          value: 'py'
        }
      ],
      output: [
        {
          field: 'abs',
          value: 'Number'
        }
      ],
      input: [
        {
          id: 'param1',
          field: 'Param 1',
          type: 'number'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Square root',
    type: 'Function',
    expression: 'Math@Function:sqrt()',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Return square root of a number'
        },
        {
          field: 'Scope',
          value: 'Math'
        },
        {
          field: 'Namespace',
          value: 'py'
        }
      ],
      output: [
        {
          field: 'squareRoot',
          value: 'Number'
        }
      ],
      input: [
        {
          id: 'param1',
          field: 'Param 1',
          type: 'number'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Average',
    type: 'Function',
    expression: 'Math@Function:average()',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Return average value of two number'
        },
        {
          field: 'Scope',
          value: 'Math'
        },
        {
          field: 'Namespace',
          value: 'py'
        }
      ],
      output: [
        {
          field: 'average',
          value: 'Number'
        }
      ],
      input: [
        {
          id: 'param1',
          field: 'Param 1',
          type: 'number'
        },
        {
          id: 'param2',
          field: 'Param 2',
          type: 'number'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Year',
    type: 'Function',
    expression: 'Date@Function:Year()',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Returns the integer year value of given date/datetime'
        },
        {
          field: 'Scope',
          value: 'Date'
        },
        {
          field: 'Namespace',
          value: 'py'
        }
      ],
      output: [
        {
          field: 'Date',
          value: 'Date'
        }
      ],
      input: [
        {
          id: 'param1',
          field: 'Param 1',
          type: 'Date'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Month',
    type: 'Function',
    expression: 'Date@Function:Month()',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Returns the integer month value of given date/datetime'
        },
        {
          field: 'Scope',
          value: 'Date'
        },
        {
          field: 'Namespace',
          value: 'py'
        }
      ],
      output: [
        {
          field: 'Date',
          value: 'Date'
        }
      ],
      input: [
        {
          id: 'param1',
          field: 'Param 1',
          type: 'Date'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Is the current browser Firefox',
    type: 'When',
    expression: '@isFirefox',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Return true if the current browser is Firefox'
        },
        {
          field: 'Scope',
          value: 'base'
        },
        {
          field: 'Namespace',
          value: 'py'
        }
      ],
      output: [
        {
          field: 'isFirefox',
          value: 'Boolean'
        }
      ]
    }
  },
  {
    id: createUID(),
    testId: createUID(),
    primary: 'Is the current browser Internet Explorer',
    type: 'When',
    expression: '@isIE',
    details: {
      primary: [
        {
          field: 'Description',
          value: 'Return true if the current browser is IE'
        },
        {
          field: 'Scope',
          value: 'base'
        },
        {
          field: 'Namespace',
          value: 'py'
        }
      ],
      output: [
        {
          field: 'isIE',
          value: 'Boolean'
        }
      ]
    }
  }
];

export const suggestedFields = items
  .filter(item => item.type === 'Field')
  .map(item => ({
    primary: item.expression.substring(1),
    id: item.expression,
    secondary: [`class: ${item.details.primary.find(({ field }) => field === 'Scope')?.value}`]
  }));

export const getScopeFromPosition = (
  cursorPosition: CursorPosition,
  autoCompleteTrigger: AutoCompleteTriggerChar,
  value: string
): string => {
  const cursorLineText = value.split('\n')[cursorPosition.line];
  const textTillCursorPos = cursorLineText.substring(0, cursorPosition.ch);
  let start = textTillCursorPos?.lastIndexOf(' ');
  if (start === -1) {
    start = 0;
  }
  const currentWord = textTillCursorPos.substring(start, cursorPosition.ch);

  let regexp;
  if (autoCompleteTrigger === '.') {
    regexp = /[\w$]*(\.+[\w$)[\](]*)+/g;
  } else {
    regexp = /[\w@'-]*[@:]/g;
  }

  const tokenArray = [...currentWord.matchAll(regexp)];
  let queryToken = '';
  if (tokenArray.length > 0) {
    const queryString = tokenArray[tokenArray.length - 1][0];
    queryToken = queryString.substring(0, queryString.length - 1);
  }
  return queryToken;
};

export const getSuggestedFunction = (scope: string) => {
  const functionList = items.filter(item => item.type === 'Function');

  let scopeBasedList = functionList;
  if (scope !== '') {
    scopeBasedList = functionList.filter(
      item => item.details.primary.find(obj => obj.field === 'Scope')?.value === scope
    );
  }

  return scopeBasedList.map(item => {
    const expressionList = item.expression.split(':');
    const paramString = item.details.input
      ?.map(param => {
        return param.id;
      })
      .join(',');
    const expression = expressionList[expressionList.length - 1];
    const id = `${expression.slice(0, expression.length - 1)}${paramString})`;
    return {
      primary: expression.substring(0, expression.length - 2),
      id: `:${id}`,
      secondary: [`Scope: ${item.details.primary.find(({ field }) => field === 'Scope')?.value}`]
    };
  });
};

export const suggestedRuleType = ['@Automation:', '@Function:', '@Decision:', '@When:'].map(
  item => ({
    primary: item.substring(1, item.length - 1),
    id: item
  })
);

export const inLineErrorsMocks: ExpressionBuilderProps['inLineErrors'] = [
  {
    start: { line: 0, ch: 14 },
    end: { line: 0, ch: 17 },
    messages: ['Max function accepts 2 arguments']
  },
  {
    start: { line: 0, ch: 0 },
    end: { line: 0, ch: 4 },
    messages: ['Scope should be different', 'Math@ is not correct']
  }
];
